import { getWebApp } from './telegram';

/**
 * Приложение занимает видимую часть окна, а не весь документ.
 *
 * Единицы `dvh` учитывают сворачивающуюся адресную строку, но не клавиатуру: в
 * мобильных браузерах она перекрывает нижнюю часть страницы, а высота окна при
 * этом не меняется. `visualViewport` сообщает реально видимую часть — по ней и
 * выставляется высота приложения.
 */

/**
 * Мельче пикселя высота дрожит на каждом кадре прокрутки. Переписывать из-за
 * этого переменную — значит перекладывать страницу без причины, а перекладка
 * посреди набора текста сбивает фокус и гасит клавиатуру.
 */
const MIN_CHANGE_PX = 1;

export function trackViewportHeight(): () => void {
  const root = document.documentElement;
  const viewport = window.visualViewport;

  let height = 0;
  let offset = -1;
  let frame = 0;

  const apply = (): void => {
    const nextHeight = Math.round(viewport?.height ?? window.innerHeight);
    const nextOffset = Math.round(viewport?.offsetTop ?? 0);

    if (Math.abs(nextHeight - height) >= MIN_CHANGE_PX) {
      height = nextHeight;
      root.style.setProperty('--app-height', `${nextHeight}px`);
    }

    /*
      Смещение видимой части внутри документа. iOS сдвигает её, открывая
      клавиатуру, и закреплённое приложение уезжает вместе с ней — шапка чата
      пропадает за верхним краем экрана. Обратный сдвиг оставляет приложение на
      месте.
    */
    if (nextOffset !== offset) {
      offset = nextOffset;
      root.style.setProperty('--app-offset', `${nextOffset}px`);
    }

    /*
      Тот же случай с другой стороны: браузер прокручивает документ, чтобы
      показать поле ввода из-под клавиатуры. Приложение к этому моменту уже
      ужалось до видимой части, и такая прокрутка только уводит его верх за
      экран.
    */
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  };

  // Событий приходит по несколько на кадр — и от изменения размера, и от
  // прокрутки видимой области. Работа откладывается до кадра отрисовки.
  const schedule = (): void => {
    if (frame) {
      return;
    }
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      apply();
    });
  };

  apply();

  viewport?.addEventListener('resize', schedule);
  viewport?.addEventListener('scroll', schedule);
  window.addEventListener('orientationchange', schedule);

  /*
    В мини-приложении высоту меняет и сам Telegram: на Android клавиатура
    уменьшает окно клиента, и до `visualViewport` страницы это доходит не
    всегда. Своё событие клиента надёжнее.
  */
  const app = getWebApp();
  app?.onEvent('viewportChanged', schedule);

  return () => {
    viewport?.removeEventListener('resize', schedule);
    viewport?.removeEventListener('scroll', schedule);
    window.removeEventListener('orientationchange', schedule);
    app?.offEvent('viewportChanged', schedule);
    if (frame) {
      window.cancelAnimationFrame(frame);
    }
    root.style.removeProperty('--app-height');
    root.style.removeProperty('--app-offset');
  };
}
